/**
 * LA TRANCHE — les chiffres qui décident de la survie, et les six tirettes.
 *
 * « L'état du héros tient sur une bande étroite et permanente, jamais dans un tiroir qu'il faut
 * ouvrir » (design §3). Elle ne défile pas, elle ne dit QUE ce qui décide.
 *
 * QUATRE GROUPES, UNE SEULE LIGNE DE BASE, UNE SEULE RANGÉE (design §3). Elle a longtemps empilé
 * un nombre sur une jauge, un nombre sur un mot, un mot sur un mot et deux nombres seuls : quatorze
 * objets de même poids sur deux rangées, c'est-à-dire le tableau de bord que le §1 interdit. Les
 * groupes vont maintenant de ce qui tue à l'instant — le corps — vers ce qui coûte à la longue —
 * les avoirs —, séparés d'un filet.
 *
 * LE NOM RESTE ÉCRIT À CÔTÉ DU NOMBRE. Ne garder que les glyphes aurait fait la barre la plus
 * propre et la moins lisible : le §5 ne permet pas qu'une information tienne à une seule forme.
 * C'est la taille du nom qui baisse, jamais sa présence — et sous la place qu'il faut, ce sont les
 * mots des TIRETTES qui tombent, pas la rangée.
 *
 * ELLE PORTE AUSSI CE QUI OUVRE LES TIROIRS, et c'est volontaire : les tirettes sont la seule barre
 * de navigation du jeu, et l'avoir mise ailleurs aurait fait deux barres.
 *
 * Les états actifs (poison, saignement…) s'accrochent DESSOUS, avec LEUR NOM ÉCRIT et seulement
 * quand il y en a : ce ne sont pas des constantes du corps, et leur donner une place permanente
 * aurait fait une barre qui change de hauteur.
 */
import type { PartieEnVue } from "../api/types.js";
import { ETATS, PALIERS_DE_FAIM, dureeDite, estSurcharge, heureDite, poidsDit } from "../lexique/mots.js";
import { Glyphe, type NomDeGlyphe } from "./Glyphe.js";
import { Jauge } from "./Jauge.js";
import { t } from "../lexique/langue.js";

/** SOUS COMBIEN D'HEURES UNE ÉCHÉANCE SE MET À PESER (§3.4). Six, c'est-à-dire un repos long et un
 * pas : en dessous, on ne peut plus à la fois dormir et arriver quelque part, et c'est exactement
 * l'instant où le choix devient intéressant. */
const HEURES_DE_VEILLE = 6;

/**
 * UNE MESURE : un glyphe, un nom, une valeur, dans cet ordre et sur une ligne. C'est la brique
 * unique de la tranche — tant qu'il n'y en a qu'une, la ligne de base ne peut pas se casser.
 */
function Mesure({
  glyphe,
  nom,
  couleur,
  alerte = false,
  children,
  infobulle
}: {
  glyphe: NomDeGlyphe;
  nom: string;
  couleur?: string;
  alerte?: boolean;
  children: React.ReactNode;
  infobulle?: string;
}) {
  return (
    <span className={alerte ? "mesure mesure--alerte" : "mesure"} title={infobulle ?? nom}>
      <Glyphe nom={glyphe} taille={15} titre={nom} couleur={couleur} />
      <span className="mesure__nom capitales">{nom}</span>
      <span className="mesure__valeur">{children}</span>
    </span>
  );
}

export function BandeDEtat({ partie, tirettes }: { partie: PartieEnVue; tirettes?: React.ReactNode }) {
  const { heros, horloge } = partie;
  const faim = PALIERS_DE_FAIM[Math.min(horloge.faim, PALIERS_DE_FAIM.length - 1)] ?? "Famine";
  const enDanger = heros.pv <= heros.pvMax * 0.25;

  return (
    <div className="bande">
      <div className="bande__interieur">
        <div className="bande__mesures">
          {/* LE CORPS — ce qui tue dans le tour qui vient. */}
          <div className="bande__groupe">
            <Mesure
              glyphe="vie"
              nom={t("Vie")}
              couleur="var(--sang)"
              alerte={enDanger}
              infobulle={t("{pv} points de vie sur {max}", { pv: heros.pv, max: heros.pvMax })}
            >
              <span className="nombre">
                {heros.pv}
                <span className="faible"> / {heros.pvMax}</span>
              </span>
              <Jauge valeur={heros.pv} maximum={heros.pvMax} espece="pv" largeur={56} />
            </Mesure>

            {heros.essenceMax > 0 ? (
              <Mesure
                glyphe="essence"
                nom={t("Essence")}
                couleur="var(--essence)"
                infobulle={t("{essence} d'Essence sur {max}", { essence: heros.essence, max: heros.essenceMax })}
              >
                <span className="nombre">
                  {heros.essence}
                  <span className="faible"> / {heros.essenceMax}</span>
                </span>
                <Jauge valeur={heros.essence} maximum={heros.essenceMax} espece="essence" largeur={56} />
              </Mesure>
            ) : null}
          </div>

          {/* L'HEURE — elle ne tue pas au tour, elle tue à la journée (§3.4). */}
          <div className="bande__groupe">
            <Mesure
              glyphe={horloge.nuit ? "nuit" : "jour"}
              nom={horloge.nuit ? t("Nuit") : t("Jour")}
              infobulle={horloge.nuit ? t("Il fait nuit") : t("Il fait jour")}
            >
              <span className="nombre">{horloge.jour}</span>
            </Mesure>
            <Mesure glyphe="horloge" nom={t("Heure")} infobulle={t("L'heure qu'il est dans le monde")}>
              <span className="nombre">{heureDite(horloge.heureDuJour)}</span>
            </Mesure>
            {/* CE QUI ARRIVE, ET DANS COMBIEN DE TEMPS (§3.4). C'est là qu'une échéance pèse :
                savoir que la crue tombe dans six heures change ce qu'on décide de faire de ces
                six heures-là. Elle n'apparaît que s'il en reste une — une mesure vide sur la
                tranche est un pixel pris au texte.

                LE NOM EST UN MOT D'INTERFACE, LE TITRE VA DANS L'INFOBULLE. Toutes les autres
                mesures portent un mot court et fixe ; y mettre le titre écrit par l'aventure
                laissait un modèle déformer la tranche — « Le rassemblement des bannières noires »
                repoussait l'or et la charge à la ligne suivante. Ce qui décide est le NOMBRE
                d'heures ; le nom de la chose se lit en s'arrêtant dessus. */}
            {horloge.echeance ? (
              <Mesure
                glyphe="danger"
                nom={t("Échéance")}
                alerte={horloge.echeance.dans <= HEURES_DE_VEILLE}
                infobulle={
                  horloge.heuresEnPlus > 0
                    ? t("{titre}. Les échéances déjà tombées coûtent {heures} h par déplacement.", {
                        titre: horloge.echeance.titre,
                        heures: horloge.heuresEnPlus
                      })
                    : t("{titre} — ce qui vient, et ce qui ne se rattrape pas", { titre: horloge.echeance.titre })
                }
              >
                <span className="nombre">{dureeDite(horloge.echeance.dans)}</span>
              </Mesure>
            ) : null}
          </div>

          {/* L'USURE — ce qui se paie au repos, ou ne se paie pas. */}
          <div className="bande__groupe">
            <Mesure glyphe="faim" nom={t("Faim")}>
              {faim}
            </Mesure>
            <Mesure glyphe="fatigue" nom={t("Fatigue")}>
              <span className="nombre">{horloge.fatigue}</span>
            </Mesure>
          </div>

          {/* LES AVOIRS — ils ne tuent pas, ils arbitrent. D'où le cran de gris en moins. */}
          <div className="bande__groupe bande__groupe--discret">
            <Mesure glyphe="or" nom={t("Or")} couleur="var(--or)">
              <span className="nombre">{heros.or}</span>
            </Mesure>
            <Mesure
              glyphe="poids"
              nom={t("Charge")}
              alerte={estSurcharge(heros.surcharge)}
              infobulle={
                estSurcharge(heros.surcharge) ? t("Sac trop lourd : l'Agilité en pâtit") : t("Ce que vous portez")
              }
            >
              <span className="nombre">
                {poidsDit(heros.poids)}
                <span className="faible"> / {poidsDit(heros.capacite)}</span>
              </span>
            </Mesure>
          </div>
        </div>

        {tirettes ? <div className="bande__tirettes">{tirettes}</div> : null}
      </div>

      {heros.etats.length ? (
        <div className="bande__etats">
          {heros.etats.map((e) => (
            <span key={e.etat} className="etat" title={ETATS[e.etat].effet}>
              <Glyphe nom={e.etat} taille={15} />
              {ETATS[e.etat].nom}
              {e.toursRestants !== null ? <span className="faible nombre"> {e.toursRestants}</span> : null}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
